// src/pages/finance-portal/fixed-assets/FixedAssetsDashboard.js
//
// Fixed Asset Management Engine
//
// Top-level dashboard with KPIs, asset class mix, and a quick-actions
// strip linking to the register, depreciation runs, CIP, disposals,
// and the Gemini-powered agent.

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Alert, Box, Button, Card, CardContent, Chip, Grid, IconButton,
    Paper, Skeleton, Stack, Tooltip, Typography,
} from '@mui/material';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CalculateIcon from '@mui/icons-material/Calculate';
import EngineeringIcon from '@mui/icons-material/Engineering';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import RefreshIcon from '@mui/icons-material/Refresh';
import ConstructionIcon from '@mui/icons-material/Construction';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

import axiosInstance from '../../../utils/axiosInstance';
import {
    FA_FONT, FA_ACCENT, FA_ACCENT_DARK, FA_ACCENT_BG,
    formatCurrency, formatNumber, ASSET_CLASS_LABELS, humanizeStatus,
} from './components/fixedAssetFormatters';

function StatCard({ icon, label, value, subtitle, color = FA_ACCENT, onClick }) {
    return (
        <Card
            onClick={onClick}
            sx={{
                borderRadius: '16px',
                border: '1px solid #e2e8f0',
                boxShadow: 'none',
                cursor: onClick ? 'pointer' : 'default',
                transition: 'all 0.2s ease',
                '&:hover': onClick
                    ? { borderColor: color, transform: 'translateY(-2px)', boxShadow: '0 4px 12px rgba(0,0,0,0.06)' }
                    : {},
            }}
        >
            <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5 }}>
                    <Box sx={{
                        width: 44, height: 44, borderRadius: '12px',
                        bgcolor: `${color}14`, display: 'flex',
                        alignItems: 'center', justifyContent: 'center', color,
                    }}>{icon}</Box>
                    {onClick && <ArrowForwardIcon sx={{ color: '#94a3b8' }} />}
                </Box>
                <Typography sx={{ fontFamily: FA_FONT, fontSize: '0.82rem', color: '#64748b', fontWeight: 500 }}>
                    {label}
                </Typography>
                <Typography sx={{ fontFamily: FA_FONT, fontSize: '1.6rem', fontWeight: 700, color: '#0f172a', mt: 0.5 }}>
                    {value}
                </Typography>
                {subtitle && (
                    <Typography sx={{ fontFamily: FA_FONT, fontSize: '0.78rem', color: '#64748b', mt: 0.5 }}>
                        {subtitle}
                    </Typography>
                )}
            </CardContent>
        </Card>
    );
}

function QuickAction({ icon, label, description, color, onClick }) {
    return (
        <Card
            onClick={onClick}
            sx={{
                borderRadius: '14px',
                border: '1px solid #e2e8f0',
                boxShadow: 'none',
                cursor: 'pointer',
                transition: 'all 0.18s ease',
                '&:hover': { borderColor: color, transform: 'translateY(-2px)' },
            }}
        >
            <CardContent sx={{ p: 2.25, display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                <Box sx={{
                    width: 38, height: 38, borderRadius: '10px',
                    bgcolor: `${color}14`, color, display: 'flex',
                    alignItems: 'center', justifyContent: 'center',
                }}>{icon}</Box>
                <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontFamily: FA_FONT, fontWeight: 700, color: '#0f172a' }}>{label}</Typography>
                    <Typography sx={{ fontFamily: FA_FONT, fontSize: '0.82rem', color: '#64748b', mt: 0.25 }}>
                        {description}
                    </Typography>
                </Box>
            </CardContent>
        </Card>
    );
}

export default function FixedAssetsDashboard() {
    const navigate = useNavigate();
    const [kpis, setKpis] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchAll = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await axiosInstance.get('/api/finance/fixed-assets/dashboard/kpis');
            if (res.data?.success) setKpis(res.data.data);
        } catch (e) {
            setError(e.response?.data?.message || 'Failed to load dashboard');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchAll(); }, [fetchAll]);

    const byClassEntries = Object.entries(kpis?.by_asset_class || {});
    const byStatusEntries = Object.entries(kpis?.by_status || {});

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Header */}
            <Paper elevation={0} sx={{
                background: `linear-gradient(135deg, ${FA_ACCENT} 0%, ${FA_ACCENT_DARK} 100%)`,
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
                position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <PrecisionManufacturingIcon sx={{ fontSize: 28, color: '#fff' }} />
                        </Box>
                        <Box>
                            <Typography variant="h4" sx={{ fontFamily: FA_FONT, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' } }}>
                                Fixed Asset Management
                            </Typography>
                            <Typography sx={{ fontFamily: FA_FONT, color: 'rgba(255,255,255,0.85)', fontSize: '0.92rem', mt: 0.25 }}>
                                register, depreciation, disposals, CIP, and AI insights
                            </Typography>
                        </Box>
                    </Box>
                    <Stack direction="row" spacing={1.5}>
                        <Tooltip title="Refresh">
                            <IconButton onClick={fetchAll} sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.15)' }}>
                                <RefreshIcon />
                            </IconButton>
                        </Tooltip>
                        <Button
                            variant="contained"
                            startIcon={<AutoAwesomeIcon />}
                            onClick={() => navigate('/finance-portal/fixed-assets/agent')}
                            sx={{
                                fontFamily: FA_FONT, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                                bgcolor: 'white', color: FA_ACCENT,
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.92)' },
                            }}
                        >
                            Asset Agent
                        </Button>
                    </Stack>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily: FA_FONT }}>{error}</Alert>}

                {/* KPIs */}
                <Grid container spacing={2.5} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6} md={3}>
                        {loading ? <Skeleton variant="rounded" height={140} /> : (
                            <StatCard
                                icon={<PrecisionManufacturingIcon />}
                                label="Total Assets"
                                value={formatNumber(kpis?.asset_count || 0)}
                                subtitle={`${kpis?.fully_depreciated_in_use || 0} fully depreciated still in use`}
                                onClick={() => navigate('/finance-portal/fixed-assets/assets')}
                            />
                        )}
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        {loading ? <Skeleton variant="rounded" height={140} /> : (
                            <StatCard
                                icon={<AccountBalanceIcon />}
                                label="Cost Basis"
                                value={formatCurrency(kpis?.total_cost_basis || 0)}
                                subtitle="Capitalized acquisitions"
                                color="#1e40af"
                            />
                        )}
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        {loading ? <Skeleton variant="rounded" height={140} /> : (
                            <StatCard
                                icon={<TrendingDownIcon />}
                                label="Accumulated Depreciation"
                                value={formatCurrency(kpis?.total_accumulated_depreciation || 0)}
                                subtitle="Lifetime depreciation"
                                color="#92400e"
                            />
                        )}
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        {loading ? <Skeleton variant="rounded" height={140} /> : (
                            <StatCard
                                icon={<CalculateIcon />}
                                label="Net Book Value"
                                value={formatCurrency(kpis?.total_book_value || 0)}
                                subtitle="Cost − Accumulated − Impairment"
                                color="#166534"
                            />
                        )}
                    </Grid>
                </Grid>

                {/* Quick actions */}
                <Typography sx={{ fontFamily: FA_FONT, fontSize: '0.95rem', fontWeight: 700, color: '#475569', mt: 2, mb: 1.5 }}>
                    Quick Actions
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={4}>
                        <QuickAction
                            icon={<PrecisionManufacturingIcon />}
                            color={FA_ACCENT}
                            label="Asset Register"
                            description="Browse, search, and edit assets"
                            onClick={() => navigate('/finance-portal/fixed-assets/assets')}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <QuickAction
                            icon={<CalculateIcon />}
                            color="#0891b2"
                            label="Run Monthly Depreciation"
                            description="Calculate and post period depreciation"
                            onClick={() => navigate('/finance-portal/fixed-assets/depreciation')}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <QuickAction
                            icon={<ConstructionIcon />}
                            color="#ea580c"
                            label="Construction-in-Progress"
                            description="Track project costs and capitalize"
                            onClick={() => navigate('/finance-portal/fixed-assets/cip')}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <QuickAction
                            icon={<DeleteSweepIcon />}
                            color="#dc2626"
                            label="Disposals"
                            description="Dispose assets with gain/loss"
                            onClick={() => navigate('/finance-portal/fixed-assets/disposals')}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <QuickAction
                            icon={<EngineeringIcon />}
                            color="#0d9488"
                            label="Operations"
                            description="Transfers, maintenance, physical counts"
                            onClick={() => navigate('/finance-portal/fixed-assets/operations')}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <QuickAction
                            icon={<AccountBalanceIcon />}
                            color="#7c3aed"
                            label="Reports"
                            description="Roll-forward, by location, tax schedule"
                            onClick={() => navigate('/finance-portal/fixed-assets/reports')}
                        />
                    </Grid>
                </Grid>

                {/* Asset class mix + status mix */}
                <Grid container spacing={2.5} sx={{ mt: 3 }}>
                    <Grid item xs={12} md={7}>
                        <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                            <CardContent sx={{ p: 2.5 }}>
                                <Typography sx={{ fontFamily: FA_FONT, fontSize: '0.95rem', fontWeight: 700, color: '#0f172a', mb: 2 }}>
                                    Assets by Class
                                </Typography>
                                {byClassEntries.length === 0 ? (
                                    <Typography sx={{ fontFamily: FA_FONT, color: '#94a3b8', py: 2 }}>
                                        No assets yet — create one to populate this chart.
                                    </Typography>
                                ) : (
                                    <Stack spacing={1.25}>
                                        {byClassEntries.map(([cls, count]) => (
                                            <Box key={cls} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <Typography sx={{ fontFamily: FA_FONT, fontSize: '0.85rem', color: '#475569', minWidth: 200 }}>
                                                    {ASSET_CLASS_LABELS[cls] || humanizeStatus(cls)}
                                                </Typography>
                                                <Box sx={{ flex: 1, height: 10, borderRadius: '999px', bgcolor: '#f1f5f9', overflow: 'hidden' }}>
                                                    <Box sx={{
                                                        height: '100%',
                                                        bgcolor: FA_ACCENT,
                                                        width: `${Math.min(100, count / Math.max(1, kpis.asset_count) * 100)}%`,
                                                    }} />
                                                </Box>
                                                <Typography sx={{ fontFamily: FA_FONT, fontWeight: 700, color: '#0f172a', minWidth: 40, textAlign: 'right' }}>
                                                    {count}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Stack>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={5}>
                        <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                            <CardContent sx={{ p: 2.5 }}>
                                <Typography sx={{ fontFamily: FA_FONT, fontSize: '0.95rem', fontWeight: 700, color: '#0f172a', mb: 2 }}>
                                    Status Mix
                                </Typography>
                                {byStatusEntries.length === 0 ? (
                                    <Typography sx={{ fontFamily: FA_FONT, color: '#94a3b8', py: 2 }}>—</Typography>
                                ) : (
                                    <Stack spacing={1}>
                                        {byStatusEntries.map(([status, count]) => (
                                            <Box key={status} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Chip label={humanizeStatus(status)} size="small"
                                                    sx={{ fontFamily: FA_FONT, bgcolor: FA_ACCENT_BG, color: FA_ACCENT_DARK, fontWeight: 600 }} />
                                                <Typography sx={{ fontFamily: FA_FONT, fontWeight: 700, color: '#0f172a' }}>{count}</Typography>
                                            </Box>
                                        ))}
                                    </Stack>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Box>
        </Box>
    );
}
